import React from 'react'
import { Play, Square, RotateCcw } from 'lucide-react'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip'
import { cn } from '@/lib/utils'

interface DashboardControlsProps {
  gameControls: {
    startGame: () => Promise<boolean>
    endGame: () => Promise<boolean>
    handleResetGame: () => Promise<boolean>
    canStartGame: boolean | undefined
    canEndGame: boolean
    canResetGame: boolean
    isSimulating?: boolean
    loading: {
      isStarting: boolean
      isEnding: boolean
      isResetting: boolean
    }
  }
}

const getTooltipText = (
  isLoading: boolean,
  loadingText: string,
  defaultText: string,
  disabledText: string,
  isSimulating?: boolean
): string => {
  if (isLoading) return loadingText
  if (isSimulating) return disabledText
  return defaultText
}

const DashboardControls: React.FC<DashboardControlsProps> = ({
  gameControls,
}) => {
  const { canStartGame, canEndGame, canResetGame, isSimulating, loading } =
    gameControls

  const isStartDisabled = !canStartGame || loading.isStarting || isSimulating

  const isEndDisabled = !canEndGame || loading.isEnding || isSimulating

  const isResetDisabled = !canResetGame || loading.isResetting || isSimulating

  return (
    <TooltipProvider>
      <div className='flex justify-center gap-3 mb-4'>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={gameControls.startGame}
              disabled={isStartDisabled}
              className={cn(
                'flex items-center justify-center w-10 h-10 border border-green-400/30 rounded-full transition-all duration-200',
                !isStartDisabled
                  ? 'bg-green-500/20 hover:bg-green-500/30 hover:scale-110'
                  : 'bg-gray-500/20 cursor-not-allowed'
              )}
            >
              {loading.isStarting ? (
                <div className='w-4 h-4 border-2 border-green-400 border-t-transparent rounded-full animate-spin' />
              ) : (
                <Play
                  className={cn(
                    'w-4 h-4',
                    !isStartDisabled ? 'text-green-400' : 'text-gray-400'
                  )}
                />
              )}
            </button>
          </TooltipTrigger>
          <TooltipContent>
            <p>
              {getTooltipText(
                loading.isStarting,
                'Starting...',
                'Start Game',
                'Start disabled during simulation',
                isSimulating
              )}
            </p>
          </TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={gameControls.endGame}
              disabled={isEndDisabled}
              className={cn(
                'flex items-center justify-center w-10 h-10 border border-red-400/30 rounded-full transition-all duration-200',
                !isEndDisabled
                  ? 'bg-red-500/20 hover:bg-red-500/30 hover:scale-110'
                  : 'bg-gray-500/20 cursor-not-allowed'
              )}
            >
              {loading.isEnding ? (
                <div className='w-4 h-4 border-2 border-red-400 border-t-transparent rounded-full animate-spin' />
              ) : (
                <Square
                  className={cn(
                    'w-4 h-4',
                    !isEndDisabled ? 'text-red-400' : 'text-gray-400'
                  )}
                />
              )}
            </button>
          </TooltipTrigger>
          <TooltipContent>
            <p>
              {getTooltipText(
                loading.isEnding,
                'Ending...',
                'Stop Game',
                'End disabled during simulation',
                isSimulating
              )}
            </p>
          </TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={gameControls.handleResetGame}
              disabled={isResetDisabled}
              className={cn(
                'flex items-center justify-center w-10 h-10 border border-yellow-400/30 rounded-full transition-all duration-200',
                !isResetDisabled
                  ? 'bg-yellow-500/20 hover:bg-yellow-500/30 hover:scale-110'
                  : 'bg-gray-500/20 cursor-not-allowed'
              )}
            >
              {loading.isResetting ? (
                <div className='w-4 h-4 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin' />
              ) : (
                <RotateCcw
                  className={cn(
                    'w-4 h-4',
                    !isResetDisabled ? 'text-yellow-400' : 'text-gray-400'
                  )}
                />
              )}
            </button>
          </TooltipTrigger>
          <TooltipContent>
            <p>
              {getTooltipText(
                loading.isResetting,
                'Resetting...',
                'Reset Game',
                'Reset disabled during simulation',
                isSimulating
              )}
            </p>
          </TooltipContent>
        </Tooltip>
      </div>
    </TooltipProvider>
  )
}

export default DashboardControls
